import { useEffect, useRef, useState } from 'react'
import {
  CHART_DEFAULT_OPTIONS,
  LABEL_FONT_SIZE,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UnitConversions,
  UserUnitSettings,
} from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { formatDateForChart, roundToDecimal } from '../../common/utils'
import { convertBaseToDisplay } from '../../common/conversions'
import { DeviceInfo, SensorProduct, UnitCategoryId } from '../../common/enums'
import CanvasJSReact from '@canvasjs/react-charts'
import { Viewport } from '../../common/types'
const { CanvasJSChart } = CanvasJSReact

interface RainfallRecord {
  timestamp: number
  value: number
}

interface DataPoints {
  rainfall: any[]
  rainfallTotals: any[]
  rainfallRate: any[]
}

interface Props {
  device: DeviceInfo
  sensorPort: string
  sensorProduct: SensorProduct
  sensorReadings: any
  viewport: Viewport
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions

  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
}

export function RainfallChart({
  device,
  sensorProduct,
  sensorPort,
  sensorReadings,
  viewport,
  userUnitSettings,
  unitConversions,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = () => {},
}: Props) {
  const ref = useRef(null)
  const [showAmounts, setShowAmounts] = useState(true)
  const [showRate, setShowRate] = useState(true)
  const [showTotals, setShowTotals] = useState(true)
  const [minX, setMinX] = useState(0)
  const [maxX, setMaxX] = useState(0)

  const [x, setX] = useState(0)
  const [y, setY] = useState(0)

  let trackDay = -1

  useEffect(() => {
    const timestamps = sensorReadings ? Object.keys(sensorReadings.readings) : []
    const minTimestamp = timestamps.length > 0 ? parseInt(timestamps[0]) * 1000 : viewport.minimum
    const maxTimestamp = timestamps.length > 0 ? parseInt(timestamps[timestamps.length - 1]) * 1000 : viewport.maximum
    const minX = viewport.minimum > minTimestamp ? viewport.minimum : minTimestamp
    const maxX = viewport.maximum < maxTimestamp ? viewport.maximum : maxTimestamp

    setMinX(minX)
    setMaxX(maxX)
  }, [viewport, sensorReadings])

  useEffect(() => {
    if (ref === null) return

    const canvas = document.getElementsByClassName('canvasjs-chart-canvas')[1]
    canvas.addEventListener('click', onClickTest)

    function onClickTest() {
      addTimestamp(x)
    }

    return () => {
      canvas.removeEventListener('click', onClickTest)
    }
  }, [ref, x, y]) // eslint-disable-line react-hooks/exhaustive-deps

  const settings = {
    rainfall: {
      userSettings: userUnitSettings[UnitCategoryId.Rainfall],
      conversions: unitConversions[UnitCategoryId.Rainfall],
    },
    rainfallRate: {
      userSettings: userUnitSettings[UnitCategoryId.RainfallRate],
      conversions: unitConversions[UnitCategoryId.RainfallRate],
    },
  }

  const units = {
    rainfall: settings.rainfall.conversions.abbreviations[settings.rainfall.userSettings.unitId],
    rainfallRate: settings.rainfallRate.conversions.abbreviations[settings.rainfallRate.userSettings.unitId],
  }

  const formulas = {
    rainfall: settings.rainfall.conversions.formulas[settings.rainfall.userSettings.unitId],
    rainfallRate: settings.rainfallRate.conversions.formulas[settings.rainfallRate.userSettings.unitId],
  }

  const precision = {
    rainfall: settings.rainfall.userSettings.precision,
    rainfallRate: settings.rainfallRate.userSettings.precision,
  }

  let maxRainfall = 0
  let maxRainfallTotal = 0
  let maxRainfallRate = 0
  let maxYAxis = 0.1
  let total = 0

  let rainfallHourlyWindow: RainfallRecord[] = []

  const dataPoints = generateDataPoints()

  function generateDataPoints() {
    const dataPoints: DataPoints = {
      rainfall: [],
      rainfallTotals: [],
      rainfallRate: [],
    }

    if (!sensorReadings) {
      return dataPoints
    }

    Object.keys(sensorReadings.readings).forEach((key: string) => {
      const ts = Number(key)
      const readingInMeters = Number(sensorReadings.readings[ts])

      const volume = convertBaseToDisplay(readingInMeters, formulas.rainfall)
      total += volume

      // get rainfall rate (base units = mm/minute)
      rainfallHourlyWindow.push({ timestamp: ts, value: readingInMeters })
      rainfallHourlyWindow = rainfallHourlyWindow.filter((record) => record.timestamp > ts - 3600)
      const ratePerHour = rainfallHourlyWindow.reduce((acc, record) => {
        return acc + record.value
      }, 0)

      const rateInMillimetersPerHour = ratePerHour * 1000
      const rateInMillimetersPerMinute = rateInMillimetersPerHour / 60
      const rate = convertBaseToDisplay(rateInMillimetersPerMinute, formulas.rainfallRate)

      if (total > maxRainfallTotal) maxRainfallTotal = total
      if (volume > maxRainfall) maxRainfall = volume
      if (rate > maxRainfallRate) maxRainfallRate = rate

      const timestamp = convertTimestamp(ts)

      dataPoints.rainfall.push({
        label: timestamp,
        x: ts * 1000,
        y: volume,
      })

      dataPoints.rainfallTotals.push({
        label: timestamp,
        x: ts * 1000,
        y: total,
      })

      dataPoints.rainfallRate.push({
        label: timestamp,
        x: ts * 1000,
        y: rate,
      })
    })

    maxYAxis = Math.max(showAmounts ? maxRainfall : 0, showTotals ? maxRainfallTotal : 0, 0.01)
    return dataPoints
  }

  const rainfall = {
    name: 'Amount',
    axisYType: 'secondary',
    type: 'stepArea',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'blue',
    fillOpacity: 0.4,
    dataPoints: showAmounts ? dataPoints.rainfall : [{ x: 0, y: 0 }],
  }

  const rainfallTotals = {
    name: 'Total',
    axisYType: 'secondary',
    type: 'area',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'lightgreen',
    fillOpacity: 0.4,
    dataPoints: showTotals ? dataPoints.rainfallTotals : [{ x: 0, y: 0 }],
  }

  const rainfallRate = {
    name: 'Rate',
    axisYType: 'primary',
    type: 'line',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'red',
    fillOpacity: 0.1,
    dataPoints: showRate ? dataPoints.rainfallRate : [{ x: 0, y: 0 }],
  }

  const title =
    {
      [SensorProduct.Rainfall]: 'Rainfall',
      [SensorProduct.OpticalRainSensorSAB]: 'Rainfall (ORS)',
      [SensorProduct.OpticalRainSensorPower]: 'Rainfall (ORS)',
    }[sensorProduct as string] || 'Rainfall'

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_Rainfall`,
    title: {
      text: title,
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    willReadFrequently: true,
    toolTip: {
      shared: true,

      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          const name = e.entries[i].dataSeries.name

          const precision =
            name === 'Rate' ? settings.rainfallRate.userSettings.precision : settings.rainfall.userSettings.precision
          const value = e.entries[i].dataPoint.y
          const color = e.entries[i].dataSeries.color
          const itemUnits = e.entries[i].dataSeries.name === 'Rate' ? units.rainfallRate : units.rainfall
          content += `<span style="color:${color}">${name}:</span> <strong> ${
            value !== undefined ? value.toFixed(precision) : ''
          } ${value !== undefined ? itemUnits : ''}</strong><br/>`
        }

        setX(e.entries[0].dataPoint.x)
        setY(e.entries[0].dataPoint.y)

        return content
      },
    },
    axisX: {
      labelFormatter: function (e: any) {
        const date = new Date(e.value)
        const day = date.getDay()
        const dayChanged = trackDay !== day
        trackDay = day

        return formatDateForChart(date, dayChanged)
      },
      viewportMinimum: minX || null,
      viewportMaximum: maxX || null,

      stripLines: [
        ...selectXRange.map((value) => {
          return {
            value,
            color: 'red',
            labelPlacement: 'outside',
          }
        }),

        selectXRange.length === 2 && {
          startValue: selectXRange[0],
          endValue: selectXRange[1],
          color: '#d8d8d8',
        },
      ],
    },
    axisY: [
      {
        includeZero: false,
        title: `Rainfall Rate (${units.rainfallRate})`,
        minimum: 0,
        maximum: maxRainfallRate * 1.1,
        titleFontSize: TITLE_FONT_SIZE,
        labelFontSize: LABEL_FONT_SIZE,
      },
    ],
    axisY2: [
      {
        includeZero: false,
        title: `Rainfall (${units.rainfall})`,
        minimum: 0,
        maximum: maxYAxis * 1.1,
        titleFontSize: TITLE_FONT_SIZE,
        labelFontSize: LABEL_FONT_SIZE,
      },
    ],
    data: [rainfall, rainfallTotals, rainfallRate],
    legend: {
      itemclick: function (e: any) {
        switch (e.dataSeries.name) {
          case 'Amount':
            setShowAmounts(!showAmounts)
            break
          case 'Rate':
            setShowRate(!showRate)
            break
          case 'Total':
            setShowTotals(!showTotals)
            break
        }
      },
    },
  }

  function getSubHeaderData() {
    const ts = getLastReadingTimestamp()
    const index = getLastReadingIndex()
    const data = [
      [
        `Port: ${sensorPort}: Rainfall`,
        `${new Date(ts).toLocaleString()}`,
        `Rate: ${roundToDecimal(dataPoints.rainfallRate[index].y, precision.rainfallRate)} ${units.rainfallRate}`,
        `Amount: ${roundToDecimal(dataPoints.rainfall[index].y, precision.rainfall)} ${units.rainfall}`,
        `Total: ${roundToDecimal(dataPoints.rainfallTotals[index].y, precision.rainfallRate)} ${units.rainfall}`,
      ],
    ]

    return data
  }

  function getLastReadingTimestamp(): number {
    const timestamps = dataPoints.rainfall.map((values: any) => values.x)
    return Number(timestamps[timestamps.length - 1])
  }

  function getLastReadingIndex() {
    const timestamps = dataPoints.rainfall.map((values: any) => values.x)
    return timestamps.length - 1
  }

  return (
    <CollapsibleSection title="Rainfall">
      <ChartSubHeader data={getSubHeaderData()} />
      <CanvasJSChart ref={ref} options={options} />
    </CollapsibleSection>
  )
}
